import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaImage,
  FaSave,
  FaTimes,
  FaBriefcase,
  FaGripVertical,
  FaGlobe,
  FaRobot,
  FaCogs,
  FaCodeBranch,
  FaBrain,
  FaChartLine,
  FaUsers,
  FaClock,
  FaEuroSign
} from 'react-icons/fa';
import './AdminPortfolio.css';

const CATEGORY_OPTIONS = [
  { value: 'rpa', label: 'RPA', icon: <FaRobot /> },
  { value: 'robotics', label: 'Robotics', icon: <FaCogs /> },
  { value: 'lowcode', label: 'Low-Code', icon: <FaCodeBranch /> },
  { value: 'ai', label: 'AI', icon: <FaBrain /> }
];

const METRIC_ICON_OPTIONS = [
  { value: 'chart', label: 'Chart', icon: <FaChartLine /> },
  { value: 'users', label: 'Users', icon: <FaUsers /> },
  { value: 'clock', label: 'Clock', icon: <FaClock /> },
  { value: 'euro', label: 'Euro', icon: <FaEuroSign /> },
  { value: 'robot', label: 'Robot', icon: <FaRobot /> },
  { value: 'cogs', label: 'Cogs', icon: <FaCogs /> },
  { value: 'brain', label: 'Brain', icon: <FaBrain /> }
];

const getMetricIcon = (iconValue) => {
  const option = METRIC_ICON_OPTIONS.find(opt => opt.value === iconValue);
  return option ? option.icon : <FaChartLine />;
};

const getCategoryIcon = (categoryValue) => {
  const option = CATEGORY_OPTIONS.find(opt => opt.value === categoryValue);
  return option ? option.icon : <FaRobot />;
};

const AdminPortfolio = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    title_en: '',
    title_fr: '',
    description_en: '',
    description_fr: '',
    results_en: '',
    results_fr: '',
    sector_en: '',
    sector_fr: '',
    technologies: [''],
    category: 'rpa',
    imageUrl: '',
    metrics: [
      { icon: 'chart', value: '', label_en: '', label_fr: '' }
    ],
    order: 0
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchProjects();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      const q = query(collection(db, 'portfolio'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjectsList(items);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fileName = `portfolio/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (err) {
      console.error('Upload error:', err);
      throw err;
    }
  };

  const deleteImage = async (imageUrl) => {
    if (imageUrl && imageUrl.includes('firebase')) {
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (err) {
        console.log('Image deletion error:', err);
      }
    }
  };

  // Technology handlers
  const handleTechChange = (index, value) => {
    const newTechs = [...formData.technologies];
    newTechs[index] = value;
    setFormData({ ...formData, technologies: newTechs });
  };

  const addTech = () => {
    setFormData({ ...formData, technologies: [...formData.technologies, ''] });
  };

  const removeTech = (index) => {
    const newTechs = formData.technologies.filter((_, i) => i !== index);
    setFormData({ ...formData, technologies: newTechs.length ? newTechs : [''] });
  };

  // Metric handlers
  const handleMetricChange = (index, field, value) => {
    const newMetrics = [...formData.metrics];
    newMetrics[index] = { ...newMetrics[index], [field]: value };
    setFormData({ ...formData, metrics: newMetrics });
  };

  const addMetric = () => {
    setFormData({
      ...formData,
      metrics: [...formData.metrics, { icon: 'chart', value: '', label_en: '', label_fr: '' }]
    });
  };

  const removeMetric = (index) => {
    const newMetrics = formData.metrics.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      metrics: newMetrics.length ? newMetrics : [{ icon: 'chart', value: '', label_en: '', label_fr: '' }]
    });
  };

  const resetForm = () => {
    setFormData({
      title_en: '',
      title_fr: '',
      description_en: '',
      description_fr: '',
      results_en: '',
      results_fr: '',
      sector_en: '',
      sector_fr: '',
      technologies: [''],
      category: 'rpa',
      imageUrl: '',
      metrics: [{ icon: 'chart', value: '', label_en: '', label_fr: '' }],
      order: projectsList.length
    });
    setImagePreview(null);
    setImageFile(null);
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({
      title_en: item.title_en || item.title || '',
      title_fr: item.title_fr || '',
      description_en: item.description_en || item.description || '',
      description_fr: item.description_fr || '',
      results_en: item.results_en || item.results || '',
      results_fr: item.results_fr || '',
      sector_en: item.sector_en || item.sector || '',
      sector_fr: item.sector_fr || '',
      technologies: item.technologies?.length ? item.technologies : [''],
      category: item.category || 'rpa',
      imageUrl: item.imageUrl || '',
      metrics: item.metrics?.length ? item.metrics : [{ icon: 'chart', value: '', label_en: '', label_fr: '' }],
      order: item.order || 0
    });
    setImagePreview(item.imageUrl || null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title_en.trim() || !formData.description_en.trim()) {
      setError('English title and description are required.');
      setActiveTab('en');
      return;
    }

    setSaving(true);

    try {
      let imageUrl = formData.imageUrl;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const projectData = {
        title_en: formData.title_en.trim(),
        title_fr: formData.title_fr.trim(),
        description_en: formData.description_en.trim(),
        description_fr: formData.description_fr.trim(),
        results_en: formData.results_en.trim(),
        results_fr: formData.results_fr.trim(),
        sector_en: formData.sector_en.trim(),
        sector_fr: formData.sector_fr.trim(),
        // Legacy fields
        title: formData.title_en.trim(),
        description: formData.description_en.trim(),
        results: formData.results_en.trim(),
        sector: formData.sector_en.trim(),
        technologies: formData.technologies.filter(t => t.trim() !== ''),
        category: formData.category,
        imageUrl,
        metrics: formData.metrics.filter(m => m.value.trim() !== ''),
        order: formData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'portfolio', editingId), projectData);
        setSuccess('Project updated successfully!');
      } else {
        projectData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'portfolio'), projectData);
        setSuccess('Project added successfully!');
      }

      await fetchProjects();
      resetForm();
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err.message || 'Failed to save project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title_en || item.title}"?`)) {
      return;
    }

    try {
      if (item.imageUrl) {
        await deleteImage(item.imageUrl);
      }
      await deleteDoc(doc(db, 'portfolio', item.id));
      setSuccess('Project deleted successfully!');
      await fetchProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-portfolio">
      <header className="admin-portfolio-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaBriefcase /> Portfolio Projects</h1>
      </header>

      <main className="admin-portfolio-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!showForm ? (
          <>
            <div className="portfolio-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New Project
              </button>
            </div>

            <div className="portfolio-list">
              {projectsList.length === 0 ? (
                <div className="empty-state">
                  <FaBriefcase />
                  <h3>No projects yet</h3>
                  <p>Click "Add New Project" to create your first portfolio item.</p>
                </div>
              ) : (
                projectsList.map((item, index) => (
                  <div key={item.id} className="portfolio-item">
                    <div className="portfolio-item-order">
                      <FaGripVertical />
                      <span>{index + 1}</span>
                    </div>
                    <div className="portfolio-item-image">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.title_en || item.title} />
                      ) : (
                        <div className="no-image"><FaImage /></div>
                      )}
                    </div>
                    <div className="portfolio-item-content">
                      <div className="portfolio-item-category">
                        {getCategoryIcon(item.category)}
                        <span>{CATEGORY_OPTIONS.find(c => c.value === item.category)?.label || item.category}</span>
                      </div>
                      <div className="portfolio-item-langs">
                        <span className="lang-badge en">EN: {item.title_en || item.title || 'Not set'}</span>
                        <span className="lang-badge fr">FR: {item.title_fr || 'Not set'}</span>
                      </div>
                      <p>{item.description_en || item.description}</p>
                    </div>
                    <div className="portfolio-item-actions">
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => handleDelete(item)} className="delete-btn">
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="portfolio-form-container">
            <h2>{editingId ? 'Edit Project' : 'Add New Project'}</h2>

            {/* Language Tabs */}
            <div className="language-tabs">
              <button
                type="button"
                className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
                onClick={() => setActiveTab('en')}
              >
                <FaGlobe /> English
              </button>
              <button
                type="button"
                className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
                onClick={() => setActiveTab('fr')}
              >
                <FaGlobe /> French
              </button>
            </div>

            <form onSubmit={handleSubmit} className="portfolio-form">
              {/* English Content */}
              <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (English) *</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    placeholder="e.g., Digital Transformation - European Bank"
                  />
                </div>

                <div className="form-group">
                  <label>Description (English) *</label>
                  <textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    placeholder="Describe the project..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Results (English)</label>
                  <textarea
                    value={formData.results_en}
                    onChange={(e) => setFormData({ ...formData, results_en: e.target.value })}
                    placeholder="e.g., 70% time reduction, 3 months to ROI..."
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Sector (English)</label>
                  <input
                    type="text"
                    value={formData.sector_en}
                    onChange={(e) => setFormData({ ...formData, sector_en: e.target.value })}
                    placeholder="e.g., Banking, Healthcare, Manufacturing..."
                  />
                </div>

                <div className="form-group">
                  <label>Metrics (English labels)</label>
                  {formData.metrics.map((metric, index) => (
                    <div key={index} className="metric-input-row">
                      <select
                        value={metric.icon}
                        onChange={(e) => handleMetricChange(index, 'icon', e.target.value)}
                        className="metric-icon-select"
                      >
                        {METRIC_ICON_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={metric.value}
                        onChange={(e) => handleMetricChange(index, 'value', e.target.value)}
                        placeholder="Value (e.g., 70%)"
                        className="metric-value-input"
                      />
                      <input
                        type="text"
                        value={metric.label_en}
                        onChange={(e) => handleMetricChange(index, 'label_en', e.target.value)}
                        placeholder="Label EN"
                        className="metric-label-input"
                      />
                      <button
                        type="button"
                        onClick={() => removeMetric(index)}
                        className="remove-feature-btn"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={addMetric} className="add-feature-btn">
                    <FaPlus /> Add Metric
                  </button>
                </div>
              </div>

              {/* French Content */}
              <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Title (French)</label>
                  <input
                    type="text"
                    value={formData.title_fr}
                    onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                    placeholder="e.g., Transformation Digitale - Banque Européenne"
                  />
                </div>

                <div className="form-group">
                  <label>Description (French)</label>
                  <textarea
                    value={formData.description_fr}
                    onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                    placeholder="Décrivez le projet..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Results (French)</label>
                  <textarea
                    value={formData.results_fr}
                    onChange={(e) => setFormData({ ...formData, results_fr: e.target.value })}
                    placeholder="e.g., Réduction de 70% du temps, ROI en 3 mois..."
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Sector (French)</label>
                  <input
                    type="text"
                    value={formData.sector_fr}
                    onChange={(e) => setFormData({ ...formData, sector_fr: e.target.value })}
                    placeholder="e.g., Banque, Santé, Industrie..."
                  />
                </div>

                <div className="form-group">
                  <label>Metrics (French labels)</label>
                  {formData.metrics.map((metric, index) => (
                    <div key={index} className="metric-input-row">
                      <select
                        value={metric.icon}
                        onChange={(e) => handleMetricChange(index, 'icon', e.target.value)}
                        className="metric-icon-select"
                        disabled
                      >
                        {METRIC_ICON_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={metric.value}
                        className="metric-value-input"
                        disabled
                        placeholder="Value"
                      />
                      <input
                        type="text"
                        value={metric.label_fr}
                        onChange={(e) => handleMetricChange(index, 'label_fr', e.target.value)}
                        placeholder="Label FR"
                        className="metric-label-input"
                      />
                      <div className="metric-placeholder"></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Common Fields */}
              <div className="common-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <div className="category-selector">
                      {CATEGORY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`category-option ${formData.category === option.value ? 'selected' : ''}`}
                          onClick={() => setFormData({ ...formData, category: option.value })}
                        >
                          {option.icon}
                          <span>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Technologies</label>
                  {formData.technologies.map((tech, index) => (
                    <div key={index} className="feature-input-row">
                      <input
                        type="text"
                        value={tech}
                        onChange={(e) => handleTechChange(index, e.target.value)}
                        placeholder={`Technology ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeTech(index)}
                        className="remove-feature-btn"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={addTech} className="add-feature-btn">
                    <FaPlus /> Add Technology
                  </button>
                </div>

                <div className="form-group">
                  <label>Project Image</label>
                  <div className="image-upload-area">
                    {imagePreview ? (
                      <div className="image-preview">
                        <img src={imagePreview} alt="Preview" />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setImageFile(null);
                            setFormData({ ...formData, imageUrl: '' });
                          }}
                          className="remove-image-btn"
                        >
                          <FaTimes /> Remove
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FaImage />
                        <span>Click to upload image</span>
                        <small>Max size: 5MB (JPG, PNG, WebP)</small>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          hidden
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <FaTimes /> Cancel
                </button>
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? (
                    <><FaSpinner className="spinner" /> Saving...</>
                  ) : (
                    <><FaSave /> {editingId ? 'Update' : 'Save'} Project</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPortfolio;
